import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';

import useTranslation from 'customHooks/translate';
import { getDebitors } from 'services/partners';
import {
  getReportDebitorCustomerBalance,
  getReportDebitorCustomerBalanceSummary,
  getCustomerBalanceInfoDebitorPdf,
} from 'services/reports';
import CustomDialog from 'pages/Dialogs/CustomDialog';
import {
  DEBITOR_BALANCE_INFO_SUMMARY,
  DEBITORS,
  REPORTS_INCOMES,
} from 'events/types';

import { ReportsSelect } from '../components/reportsSelect';
import { ReportsTab } from '../components/ReportsTab';
import IncomesTable from './IncomesTable';
import { IncomesSummary } from './IncomesSummary';
import { delay, openNotification, waitForReportCompletion } from 'utils';
import { CUSTOMER_DEBITOR_BALANCE_REPORT_KEY } from 'consts';

export const CustomerDebitorBalance = () => {
  const translation = useTranslation();
  const [toDate, setToDate] = useState('');
  const [partner, setPartner] = useState(0);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [confirmedToDate, setConfirmedToDate] = useState('');
  const [confirmedPartner, setConfirmedPartner] = useState(0);
  const [pageSize] = useState(10);

  // Infinite scroll related states
  const [incomesPageIndex, setIncomesPageIndex] = useState(0);
  const [incomesTableData, setIncomesTableData] = useState<any[]>([]);
  const [hasMoreIncomes, setHasMoreIncomes] = useState(true);
  const [isLoadingMoreIncomes, setIsLoadingMoreIncomes] = useState(false);
  const [open, setOpen] = useState<boolean>(true);

  // Refs for scroll containers and loading states
  const incomesTableContainerRef = useRef<HTMLDivElement>(null);
  const incomesLoadingRef = useRef<HTMLDivElement>(null);
  const isFetchingIncomesRef = useRef(false);

  const handleSelect = (value: unknown) => {
    setPartner(Number(value));
  };

  // Reset data function
  const resetData = () => {
    setIncomesTableData([]);
    setIncomesPageIndex(0);
    setHasMoreIncomes(true);
  };

  const { data: debitorsData } = useQuery<any, AxiosError, any[]>({
    queryKey: [DEBITORS],
    queryFn: () => getDebitors(),
    select: (response) =>
      response.data.data.map((debitor) => ({
        label: debitor.name,
        value: debitor.id,
      })),
  });

  // Function to fetch incomes data
  const fetchIncomesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingIncomesRef.current) return null;

      isFetchingIncomesRef.current = true;
      setIsLoadingMoreIncomes(true);

      try {
        const response = await getReportDebitorCustomerBalance(
          confirmedToDate,
          confirmedPartner,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setIncomesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataIncome}`);
            } else {
              openNotification(
                `${translation.reports.IncomesDataFound}`,
                'success'
              );
            }
          } else {
            setIncomesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : incomesTableData.length) + data.data.length;
          setHasMoreIncomes(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataIncome}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreIncomes(false);
        isFetchingIncomesRef.current = false;
      }
    },
    [
      confirmedToDate,
      confirmedPartner,
      pageSize,
      incomesTableData.length,
      translation.reports.NoDataIncome,
      translation.reports.IncomesDataFound,
    ]
  );

  useQuery({
    queryKey: [REPORTS_INCOMES, confirmedToDate, confirmedPartner],
    queryFn: () => fetchIncomesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  const { data: summaryData } = useQuery({
    queryKey: [DEBITOR_BALANCE_INFO_SUMMARY, confirmedToDate, confirmedPartner],
    queryFn: () =>
      getReportDebitorCustomerBalanceSummary(confirmedToDate, confirmedPartner),
    select: (res) => res.data,
    enabled: shouldFetch,
  });

  const setDateRange = (from: string, to: string) => {
    setToDate(to);
  };

  const handleConfirm = () => {
    if (!toDate || !partner) {
      openNotification(`${translation.reports.Parameters}`, 'error');
      return;
    }
    setConfirmedToDate(toDate);
    setConfirmedPartner(partner);
    resetData();
    setShouldFetch(true);
    setOpen(false);
  };

  const handlePdfExport = async () => {
    try {
      openNotification(
        `${translation.reports.PdfWait}`,
        'info',
        0,
        CUSTOMER_DEBITOR_BALANCE_REPORT_KEY,
        true
      );

      const excelResponse = await getCustomerBalanceInfoDebitorPdf(
        confirmedToDate,
        confirmedPartner
      );

      const reportId = excelResponse?.data?.id;

      if (reportId) {
        await delay(4000);

        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );

        const blob = new Blob([downloadResponse.data], {
          type: 'application/pdf',
        });
        saveAs(blob, `report_${reportId}.pdf`);

        openNotification(
          `${translation.reports.PdfGenerated}`,
          'success',
          2,
          CUSTOMER_DEBITOR_BALANCE_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          CUSTOMER_DEBITOR_BALANCE_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        CUSTOMER_DEBITOR_BALANCE_REPORT_KEY
      );
    }
  };

  // Handle incomes scroll
  const handleIncomesScroll = useCallback(() => {
    if (
      !incomesTableContainerRef.current ||
      !incomesLoadingRef.current ||
      isFetchingIncomesRef.current ||
      !hasMoreIncomes
    )
      return;

    const containerRect =
      incomesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = incomesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = incomesPageIndex + 1;
      setIncomesPageIndex(nextPage);
      fetchIncomesData(nextPage);
    }
  }, [incomesPageIndex, hasMoreIncomes, fetchIncomesData]);

  // Set up scroll event listeners
  useEffect(() => {
    const incomesContainer = incomesTableContainerRef.current;
    if (incomesContainer) {
      incomesContainer.addEventListener('scroll', handleIncomesScroll);
      return () => {
        incomesContainer.removeEventListener('scroll', handleIncomesScroll);
      };
    }
  }, [handleIncomesScroll]);

  const isShowButtons = incomesTableData.length > 0;

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
      <div className="col-span-4 mb-[20px] md:mb-[0px]">
        {(!!confirmedToDate || !!confirmedPartner) && (
          <div className="my-[12px] px-[16px] md:px-[32px]">
            <div className="flex flex-wrap items-center gap-[8px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[12px] text-[#4B4B4B] md:text-[13px]">
              <span className="font-semibold text-[#1D1D1D]">
                {translation.filter}:
              </span>
              {confirmedToDate && (
                <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                  {confirmedToDate}
                </span>
              )}
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.subusers.debitor}:{' '}
                </span>
                {
                  debitorsData?.find((option) => partner === option.value)
                    ?.label
                }
              </span>
            </div>
          </div>
        )}
        <IncomesTable
          incomesTableContainerRef={incomesTableContainerRef}
          incomesTableData={incomesTableData}
          incomesLoadingRef={incomesLoadingRef}
          isLoadingMoreIncomes={isLoadingMoreIncomes}
          hasMoreIncomes={hasMoreIncomes}
          setOpen={setOpen}
        />
      </div>
      {isShowButtons && (
        <div className="h-full  md:border-l md:border-l-[#f5f7f9]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px] px-[16px] md:pt-[12px]">
            <button
              onClick={handlePdfExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsPdf}</p>
            </button>
          </div>
          {!!summaryData && (
            <IncomesSummary
              title={translation.reports.SummaryIncome}
              summaryData={summaryData}
            />
          )}
        </div>
      )}

      <CustomDialog
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        title={translation.filter}
        buttonTitle={translation.reports.BtnConfirm}
        onSubmit={handleConfirm}
      >
        <ReportsTab
          setDateRange={setDateRange}
          hasFromDateField={false}
          hasTabs={false}
        />
        <ReportsSelect
          title={translation.subusers.debitor}
          onChange={handleSelect}
          options={debitorsData || []}
        />
      </CustomDialog>
    </div>
  );
};

export default CustomerDebitorBalance;
